/**
 * An amount of money as a person reads it: always exactly TWO decimals, grouped in the reader's
 * language (decision M-34: nothing is ever below a cent, and every screen shows a cent). The API
 * writes a money amount with two decimals; this is the one place a screen turns it into text when
 * it shows no currency symbol (`formatMoney` in `datetime.ts` is the one with the symbol).
 * A rate or a percentage is not money and does not go through here.
 */
import { readerLocale } from '../i18n/language';

export function formatCents(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat(readerLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * What a person typed in a money field, with anything past the second decimal dropped as it is
 * typed ("10.123" becomes "10.12"): the field itself never holds a fraction of a cent, so no
 * message has to talk about one. A comma is a thousands separator and is left alone.
 */
export function limitToCents(typed: string): string {
  const point = typed.indexOf('.');
  return point === -1 ? typed : typed.slice(0, point + 3);
}

/**
 * Whether a typed amount is under a series' minimum contribution (owner, 2026-10-30): the server
 * refuses a new contribution below it (`contribution_below_minimum`), so the form says so before
 * it is sent. A series with no minimum (none, or zero) refuses nothing.
 */
export function isBelowMinimum(amount: string, minimum: string | null | undefined): boolean {
  const floor = Number(minimum ?? 0);
  const typed = Number(amount);
  return Number.isFinite(floor) && floor > 0 && Number.isFinite(typed) && typed > 0 && typed < floor;
}
